import * as api from "@/api/joint-bill";
import { useEffect, useRef, useState } from "react";

export interface FilterParams {
  all: "down" | false;
  sale: "up" | "down" | false;
  brand: "down" | false;
  price: "down" | false;
}

export interface Search {
  all: string | undefined;
  sale: string | undefined;
  brand: any[] | undefined;
  price: object | undefined;
}

export const useProductsControl = (actId: string) => {
  const [filter, setFilter] = useState<FilterParams>({
    all: "down",
    sale: false,
    brand: false,
    price: false,
  });
  const [searchParams, setSearchParams] = useState<Search>({
    all: "down",
    sale: undefined,
    brand: undefined,
    price: undefined,
  });
  const [pageNum, setPageNum] = useState<number>(1);
  const [pageSize] = useState<number>(6);
  const [hasNext, setHasNext] = useState<boolean>(true);
  const [productList, setProductList] = useState<any[]>([]);
  const [brandList, setBrandList] = useState<any[]>([]);
  const [priceRangeList, setPriceRangeList] = useState<any[]>([]);
  const loading = useRef<boolean>(false);

  // 根据搜索参数更改筛选栏状态
  const changeFilterStatus = (param: Search) => {
    const { all, sale, brand, price } = param;
    const filter: FilterParams = {
      all: false,
      sale: false,
      brand: false,
      price: false,
    };
    if (all === "down") {
      filter.all = "down";
    }
    if (sale) {
      filter.sale = sale === "up" ? "up" : "down";
    }
    if (brand && brand.length > 0) {
      filter.brand = "down";
    }
    if (price) {
      filter.price = "down";
    }
    // 检测是否全部为false
    const res = Object.keys(filter)
      .map(item => filter[item])
      .reduce((total, item) => item || total);
    if (!res) {
      filter.all = "down";
    }
    setFilter(filter);
  };

  // 参数转换
  const dataConversion = (params: Search) => {
    const { all, sale, brand, price } = params;
    const resParams: any = {
      pageNum: pageNum,
      pageSize: pageSize,
      scene: "JOIN_ORDER_PAGE", // 为赠品活动场景设置特定标识
      soldOutStatus: false,
      promoActivityIds: [actId],
    };

    if (all) {
      resParams.sortModel = 1;
      resParams.sortType = 2;
    }
    if (sale) {
      resParams.sortModel = 3;
      resParams.sortType = sale === "up" ? 1 : 2;
    }
    if (brand && brand.length > 0) {
      resParams.brandIds = brand;
    }
    if (price) {
      resParams.priceLow = (price as any).min;
      resParams.priceHigh = (price as any).max;
    }

    return { searchParams: resParams };
  };

  // 获取品牌列表
  const getBrandList = async () => {
    try {
      // 使用filterData API获取筛选数据，包括品牌列表
      const res: any = await api.filterData({
        scene: "JOIN_ORDER_PAGE",
        needBrandWhenOnlyOne: true,
        pageNum: 1,
        pageSize: 10,
        promoActivityIds: [actId],
      });
      if (res?.success) {
        if (Array.isArray(res?.data?.brandList)) {
          setBrandList(res.data.brandList);
        }
        if (Array.isArray(res?.data?.priceRangeList)) {
          setPriceRangeList(res.data.priceRangeList);
        }
      }
    } catch (error) {
      console.error("获取筛选数据失败", error);
    }
  };

  // 查询商品列表
  const handleSearch = async (params?: Search, init = false) => {
    if (loading.current) {
      return;
    }
    loading.current = true;
    const param = dataConversion(params || searchParams);
    changeFilterStatus(params || searchParams);
    if (init) {
      setPageNum(1);
      setProductList([]);
      setHasNext(true);
      param.searchParams.pageNum = 1;
    }

    try {
      // 使用与优惠凑单页相同的API
      const res: any = await api.queryProductList(param);

      if (res?.success && Array.isArray(res?.data?.result)) {
        const list = init ? res?.data?.result : productList.concat(res?.data?.result);
        setProductList(list);
        setHasNext(res?.data?.hasNext ?? false);
        setPageNum(res?.data?.pageIndex + 1);
      } else {
        setHasNext(false);
      }
    } catch (error) {
      console.error("获取商品列表失败", error);
      setHasNext(false);
    } finally {
      loading.current = false;
    }
  };

  // 商品点击埋点记录
  const secondResourceClick = (item: any) => {
    console.log("商品点击", item);
    // 这里可以实现埋点逻辑
  };

  // 初始化数据
  useEffect(() => {
    // 获取品牌列表
    getBrandList();
    // 初始化页面时加载商品数据
    handleSearch();
  }, []);

  return {
    filter,
    searchParams,
    setSearchParams,
    handleSearch,
    productList,
    hasNext,
    brandList,
    priceRangeList,
    secondResourceClick,
  };
};
